import type { MembershipItem, MembershipListItem } from '@/shared/lib';

/** Option of a member select: the value is the membership id, never the user id. */
export interface MemberOption {
  id: number;
  name: string;
  registrationNumber?: string | null;
}

/**
 * The two mappers that build these objects disagree on what `id` means: user id in
 * `mapBranchMemberToUser`, member id in `mapBranchMemberToSelectionItem`. These helpers therefore
 * never read `id` and always go through the memberships.
 */
export type CollaboratorLike = {
  id?: number;
  userId?: number;
  name?: string;
  registrationNumber?: string | null;
  membership?: MembershipItem | null;
  memberships?: MembershipListItem[];
  positions?: { id: number; name: string }[];
  userPositions?: { positionId?: number; position?: { id: number; name: string } }[];
};

export type MembershipLike = {
  id: number;
  branchId?: number;
  branch?: { id: number };
  positionId?: number;
  position?: { id: number; name?: string };
  positions?: { id: number; name?: string }[];
};

export const mapNamedPositions = (positions: Array<{ id: number; name?: string }>) =>
  positions
    .filter((position): position is { id: number; name: string } => Boolean(position.name))
    .map((position) => ({ id: position.id, name: position.name }));

/**
 * `membership` and `memberships` are two spellings of the same thing depending on which mapper
 * built the object, so both are read and deduplicated by membership id.
 */
export function collectBranchMemberships(
  user: CollaboratorLike,
  branchId: number | undefined
): MembershipLike[] {
  const seen = new Set<number>();
  const memberships: MembershipLike[] = [];
  const push = (membership: MembershipLike | null | undefined) => {
    if (!membership?.id || seen.has(membership.id)) {
      return;
    }
    seen.add(membership.id);
    memberships.push(membership);
  };

  user.memberships?.forEach((membership) => push(membership as MembershipLike));
  if (user.membership) {
    push(user.membership as MembershipLike);
  }

  if (branchId === undefined) {
    return memberships;
  }

  return memberships.filter((membership) => Number(membership.branchId) === Number(branchId));
}

export function deduplicatePositions(
  positions: Array<{ id: number; name: string }>
): Array<{ id: number; name: string }> {
  return positions.filter((position, index, self) => {
    return index === self.findIndex((candidate) => candidate.id === position.id);
  });
}

/**
 * Keyed by membership and not by user because a user can be a member of more than one branch, and
 * what the API stores on a decision is the member.
 */
export function buildBranchMemberOptions(
  users: CollaboratorLike[],
  branchId: number | undefined
): MemberOption[] {
  const byId = new Map<number, MemberOption>();

  for (const user of users) {
    for (const membership of collectBranchMemberships(user, branchId)) {
      byId.set(membership.id, {
        id: membership.id,
        name: user.name ?? '',
        registrationNumber: user.registrationNumber ?? null,
      });
    }
  }

  return [...byId.values()];
}

/**
 * Scoped to the branch, so a member of another branch contributes nothing even when the same user
 * appears in the pool.
 */
export function getMemberPositionsFromUsers(
  memberId: number | string,
  users: CollaboratorLike[],
  branchId: number | undefined
): Array<{ id: number; name: string }> {
  const numId = typeof memberId === 'string' ? Number.parseInt(memberId, 10) : memberId;
  if (!numId || Number.isNaN(numId)) {
    return [];
  }

  const user = users.find((candidate) =>
    collectBranchMemberships(candidate, branchId).some((membership) => membership.id === numId)
  );
  if (!user) {
    return [];
  }

  const membership = collectBranchMemberships(user, branchId).find((item) => item.id === numId);

  if (!membership) {
    return [];
  }

  const positions = membership.positions ?? (membership.position ? [membership.position] : []);
  return deduplicatePositions(mapNamedPositions(positions));
}
